<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import dayjs from "dayjs";
import { RuleDimension } from "@/modules/rule_engine/ruleEngineClasses";
import { useI18n } from "vue-i18n";
import { numericAndFloat, required } from "@/ui/validation/validationRules";
import ConfigureConditionComparisonSelectionDatePicker from "@/modules/rule_engine/components/conditions/ConfigureConditionComparisonSelectionDatePicker.vue";
import { parse } from "chartjs-graph";
const dateFormat = "YYYY-MM-DD HH:mm:ss";

const { t } = useI18n();

const props = defineProps<{
	type: "between_date" | "between_number";
	selectedRuleDimension: RuleDimension;
}>();

export type BetweenSelection = {
	selectedFrom: string | number;
	selectedTo: string | number;
};

const selection = defineModel<BetweenSelection>();
const isInputValid = defineModel<boolean>("isInputValid", {
	default: () => ref(true),
});

/**
 * Seeds a week-long range so both fields open populated rather than empty.
 *
 * One assignment covering both bounds: the parent binds `selection` to a
 * computed that rebuilds its object on every read, so mutating a property
 * writes into a throwaway; and two successive writes would clobber each other,
 * because `defineModel` keeps its local value until the new prop arrives.
 */
onBeforeMount(() => {
	if (props.type !== "between_date") return;

	const needsFrom = !isDateString(selection.value.selectedFrom);
	const needsTo = !isDateString(selection.value.selectedTo);

	if (!needsFrom && !needsTo) return;

	selection.value = {
		selectedFrom: needsFrom
			? dayjs(new Date()).startOf("day").format(dateFormat)
			: selection.value.selectedFrom,
		selectedTo: needsTo
			? dayjs(new Date()).add(7, "day").startOf("day").format(dateFormat)
			: selection.value.selectedTo,
	};
});

const isDateString = (value: unknown): boolean => {
	if (typeof value !== "string") return false;

	return parse(value)?.isValid();
};

const selectedFrom = computed({
	get() {
		return selection.value.selectedFrom ?? 0;
	},
	set(newValue: string | number | Date) {
		let parsedValue: string | number | Date;

		if (props.type === "between_number") {
			parsedValue =
				typeof newValue === "string" ? parseFloat(newValue) : Number(newValue);
			if (isNaN(parsedValue)) parsedValue = 0;
		} else {
			parsedValue = String(newValue);
		}

		selection.value = {
			selectedTo: selection.value.selectedTo,
			selectedFrom: parsedValue,
		};
	},
});

const selectedTo = computed({
	get() {
		return selection.value.selectedTo ?? 0;
	},
	set(newValue: string | number | Date) {
		let parsedValue: string | number | Date;

		if (props.type === "between_number") {
			parsedValue =
				typeof newValue === "string" ? parseFloat(newValue) : Number(newValue);
			if (isNaN(parsedValue)) parsedValue = 0;
		} else {
			parsedValue = String(newValue);
		}

		selection.value = {
			selectedFrom: selection.value.selectedFrom,
			selectedTo: parsedValue,
		};
	},
});
</script>

<template>
	<v-form v-model="isInputValid">
		<div v-if="props.type === 'between_number'" class="d-flex ga-2 align-start">
			<v-text-field
				class="bound"
				placeholder="Numeric value"
				v-model="selectedFrom"
				variant="filled"
				density="compact"
				hide-details="auto"
				:rules="[required(t), numericAndFloat(t)]"
			/>
			<div class="separator">and</div>
			<v-text-field
				class="bound"
				placeholder="Numeric value"
				v-model="selectedTo"
				variant="filled"
				density="compact"
				hide-details="auto"
				:rules="[required(t), numericAndFloat(t)]"
			>
				<template v-slot:append>
					<v-tooltip>
						<template v-slot:activator="{ props }">
							<v-icon v-bind="props" size="18">mdi-help-circle-outline</v-icon>
						</template>
						<span>
							When specifying a from and to, the to is exclusive. For example:
							from 0 to 5 will match 0, 1, 2, 3, 4, 4.99 but not 5.
						</span>
					</v-tooltip>
				</template>
			</v-text-field>
		</div>

		<div
			v-if="props.type === 'between_date'"
			class="d-flex ga-2 align-start flex-wrap"
		>
			<configure-condition-comparison-selection-date-picker
				class="bound"
				v-model="selectedFrom"
				v-model:is-input-valid="isInputValid"
				:show-help="false"
			/>
			<div class="separator">and</div>
			<configure-condition-comparison-selection-date-picker
				class="bound"
				v-model="selectedTo"
				:round-up="true"
				v-model:is-input-valid="isInputValid"
			/>
		</div>
	</v-form>
</template>

<style scoped lang="scss">
/*
 * Both bounds share the row evenly, but never below their own min-width — the
 * date pickers wrap onto separate lines rather than squeezing.
 */
.bound {
	flex: 1 1 0;
	min-width: 0;
}

.separator {
	flex: 0 0 auto;
	padding-top: 0.5rem;
	opacity: 0.7;
}
</style>
